<template>
  <div>
    <div class="content-panel">
      <!-- ===== 表格 ===== -->
      <div class="product-table-wrap">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-state">
          <span class="loading-spinner"></span>
          <p>{{ $t("withdrawal.loading") }}</p>
        </div>

        <!-- 空状态 -->
        <div v-else-if="withdrawalRecords.length === 0" class="empty-state">
          <span class="empty-icon">📭</span>
          <p>{{ $t("withdrawal.empty") }}</p>
        </div>

        <!-- 表格 -->
        <table v-else class="product-table">
          <thead>
            <tr>
              <th>{{ $t("withdrawal.coin") }}</th>
              <th>{{ $t("withdrawal.quantity") }}</th>
              <th>{{ $t("withdrawal.network") }}</th>
              <th>{{ $t("withdrawal.address") }}</th>
              <th>{{ $t("withdrawal.status") }}</th>
              <th>{{ $t("withdrawal.time") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in withdrawalRecords" :key="index">
              <td>
                <span class="status-badge status-active">{{ item.name }}</span>
              </td>
              <td>
                <div class="text-reduce">
                  -{{ formatNumber(item.real_number) }}
                </div>
              </td>
              <td>{{ item.network }}</td>
              <td class="address-cell">{{ item.transfer_token }}</td>
              <td>
                <span class="status-badge" :class="getStatusClass(item.status)">
                  {{ getStatusText(item.status) }}
                </span>
              </td>
              <td>{{ item.create_time }}</td>
            </tr>
          </tbody>
        </table>

        <!-- ===== 分页 ===== -->
        <div v-if="total > 0" class="pagination-wrap">
          <div class="pagination-info">
            {{ $t("withdrawal.paginationInfo", { total, page, totalPages }) }}
          </div>
          <div class="pagination-buttons">
            <button
              class="page-btn"
              :disabled="page <= 1"
              @click="changePage(1)"
            >
              {{ $t("withdrawal.firstPage") }}
            </button>
            <button
              class="page-btn"
              :disabled="page <= 1"
              @click="changePage(page - 1)"
            >
              {{ $t("withdrawal.prevPage") }}
            </button>

            <button
              v-for="p in pageNumbers"
              :key="p"
              class="page-btn"
              :class="{ active: p === page }"
              @click="changePage(p)"
            >
              {{ p }}
            </button>

            <button
              class="page-btn"
              :disabled="page >= totalPages"
              @click="changePage(page + 1)"
            >
              {{ $t("withdrawal.nextPage") }}
            </button>
            <button
              class="page-btn"
              :disabled="page >= totalPages"
              @click="changePage(totalPages)"
            >
              {{ $t("withdrawal.lastPage") }}
            </button>
          </div>
          <div class="pagination-size">
            <select
              v-model="size"
              @change="handleSizeChange"
              class="size-select"
            >
              <option :value="10">{{ $t("withdrawal.perPage", { count: 10 }) }}</option>
              <option :value="20">{{ $t("withdrawal.perPage", { count: 20 }) }}</option>
              <option :value="50">{{ $t("withdrawal.perPage", { count: 50 }) }}</option>
              <option :value="100">{{ $t("withdrawal.perPage", { count: 100 }) }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WithdrawalList",
  data() {
    return {
      // 分页参数
      page: 1,
      size: 10,
      total: 0,

      // 数据
      withdrawalRecords: [],
      loading: false,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.size);
    },
    pageNumbers() {
      const pages = [];
      const total = this.totalPages;
      const current = this.page;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        if (current > 3) {
          pages.push("...");
        }
        for (
          let i = Math.max(2, current - 1);
          i <= Math.min(total - 1, current + 1);
          i++
        ) {
          pages.push(i);
        }
        if (current < total - 2) {
          pages.push("...");
        }
        pages.push(total);
      }
      return pages;
    },
  },
  created() {
    this.getWithdrawalRecords();
  },
  methods: {
    // ===== 获取提现记录 =====
    getWithdrawalRecords() {
      this.loading = true;

      this.$api.userWallet
        .getTransferList({
          page: this.page,
          size: this.size,
        })
        .then((res) => {
          if (res.code === 0) {
            this.withdrawalRecords = res.data.data || [];
            this.total = res.data.total || 0;
            this.page = res.data.current || 1;
            this.size = res.data.pageSize || 10;
          } else {
            console.error("获取提现记录失败:", res.msg);
            this.withdrawalRecords = [];
            this.total = 0;
          }
        })
        .catch((error) => {
          console.error("接口请求异常:", error);
          this.withdrawalRecords = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // ===== 状态文字 =====
    getStatusText(status) {
      const map = {
        1: this.$t("withdrawal.statusProcessing"),
        2: this.$t("withdrawal.statusSuccess"),
        3: this.$t("withdrawal.statusFailed"),
      };
      return map[status] || this.$t("withdrawal.statusUnknown");
    },

    // ===== 状态样式 =====
    getStatusClass(status) {
      const map = {
        1: "status-pending",
        2: "status-completed",
        3: "status-canceled",
      };
      return map[status] || "status-unknown";
    },

    // ===== 数据格式化 =====
    formatNumber(value) {
      if (value === undefined || value === null || isNaN(value)) {
        return "0.00";
      }
      return Number(value).toFixed(2);
    },

    // ===== 分页 =====
    changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return;
      this.page = page;
      this.getWithdrawalRecords();
    },

    // ===== 每页条数变化 =====
    handleSizeChange() {
      this.page = 1;
      this.getWithdrawalRecords();
    },
  },
};
</script>

<style scoped>
/* ===== 表格 ===== */
.product-table-wrap {
  overflow-x: auto;
}

.product-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.product-table thead th {
  text-align: left;
  padding: 12px 10px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
}

.product-table tbody td {
  padding: 14px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
}

.product-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

.address-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-family: "SF Mono", "Courier New", monospace;
}

/* ===== 状态标签 ===== */
.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-active {
  background: rgba(0, 180, 255, 0.15);
  color: #4fc3ff;
}

.status-completed {
  background: rgba(0, 200, 150, 0.15);
  color: #4ade80;
}

.status-pending {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.status-canceled {
  background: rgba(255, 82, 82, 0.15);
  color: #ff6b6b;
}

.status-unknown {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.4);
}

/* ===== 金额颜色 ===== */
.text-reduce {
  color: #ff5722 !important;
  padding: 5px 10px;
  background: #ff572220;
  border-radius: 15px;
  display: inline-block;
  font-weight: 600;
}

/* ===== 加载状态 ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.3);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #4fc3ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.3);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

/* ===== 分页 ===== */
.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 16px;
  flex-wrap: wrap;
}

.pagination-info {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  white-space: nowrap;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.page-btn {
  padding: 6px 12px;
  min-width: 32px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.page-btn:hover:not(:disabled):not(.active) {
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-btn.active {
  background: rgba(0, 180, 255, 0.2);
  border-color: rgba(0, 180, 255, 0.3);
  color: #4fc3ff;
}

.pagination-size {
  display: flex;
  align-items: center;
}

.size-select {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  outline: none;
  cursor: pointer;
}

.size-select:focus {
  border-color: rgba(0, 180, 255, 0.3);
}

.size-select option {
  background: #1a1a2e;
  color: #fff;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .product-table thead th,
  .product-table tbody td {
    padding: 10px 6px;
    font-size: 12px;
  }

  .pagination-wrap {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .pagination-info {
    font-size: 12px;
  }

  .page-btn {
    padding: 4px 10px;
    font-size: 12px;
    min-width: 28px;
  }
}

@media (max-width: 480px) {
  .product-table thead th,
  .product-table tbody td {
    padding: 8px 4px;
    font-size: 11px;
  }
}
</style>
